import { useCalendar } from "@modules/calendar/hooks/useCalendar"
import { word } from "@modules/core/utils/i18n"
import { ProgramInfinite, ProgramInfiniteTypeOf } from "@modules/programs/programInfiniteTypes"
import { ProgramOnOff, ProgramOnOffTypeOf } from "@modules/programs/programOnOffTypes"
import { useProgramSessionDuplicate } from "@modules/programSession/hooks/useProgramSessionDuplicate"
import { useProgramSessionEvents } from "@modules/training/hooks/useTraining"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { HUDialog } from "@modules/ui/components/popup/huDialog"
import dayjs from "dayjs"
import { useImperativeHandle, useState } from "react"
import { useTheme } from "styled-components"

export type ProgramWeekCopyModalRef = {
  onPasteWeekClick: () => Promise<void>
}

export type ProgramWeekCopyModalProps = {
  program: ProgramInfinite | ProgramOnOff
  ref: React.Ref<ProgramWeekCopyModalRef>
}

export const ProgramWeekCopyModal = ({ program, ref }: ProgramWeekCopyModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showModal, setShowModal] = useState(false)

  const { refreshCalendarEvents, selectedDate } = useCalendar()
  const { sessions: programSessions } = useProgramSessionEvents(program.id)
  const { onPasteInfiniteProgramWeek, onPasteOnOffProgramWeek, weekCopied } = useProgramSessionDuplicate()

  const theme = useTheme()

  const currentWeekHasSessions = programSessions.length > 0
  const copiedSessionsAreFromDifferentProgram = weekCopied?.originProgramId !== program.id

  const title = currentWeekHasSessions
    ? word("program.weekCopyPaste.paste.modale.sessionsAlreadyExist.title")
    : copiedSessionsAreFromDifferentProgram
      ? word("program.weekCopyPaste.paste.modale.pasteFromAnotherProgram.title", {
          originProgramName: weekCopied?.originProgramName,
        })
      : ""
  const message =
    copiedSessionsAreFromDifferentProgram && currentWeekHasSessions
      ? word("program.weekCopyPaste.paste.modale.pasteFromAnotherProgramAndSessionsAlreadyExist.message", {
          originProgramName: weekCopied?.originProgramName,
        })
      : currentWeekHasSessions
        ? word("program.weekCopyPaste.paste.modale.sessionsAlreadyExist.message")
        : ""

  const onHide = () => {
    setShowModal(false)
  }

  const onPasteWeek = async (overwrite?: boolean) => {
    setIsSubmitting(true)
    if (program._programType === ProgramInfiniteTypeOf && !!selectedDate && typeof selectedDate !== "number") {
      await onPasteInfiniteProgramWeek(program.id, dayjs(selectedDate).weekday(0).toISOString(), overwrite)
    } else if (program._programType === ProgramOnOffTypeOf && typeof selectedDate === "number") {
      await onPasteOnOffProgramWeek(program.id, selectedDate, overwrite)
    }
    setIsSubmitting(false)
    setShowModal(false)
    refreshCalendarEvents()
  }

  useImperativeHandle(ref, () => ({
    onPasteWeekClick: async () => {
      if (currentWeekHasSessions || copiedSessionsAreFromDifferentProgram) {
        setShowModal(true)
      } else {
        await onPasteWeek()
      }
    },
  }))

  return (
    <HUDialog
      visible={showModal}
      header={null}
      onHide={onHide}
      style={{ width: "418px", padding: 0 }}
      contentClassName="p-0"
    >
      <div className="flex flex-column gap-3 bg-neutralColor-100 pt-6 px-5 pb-2">
        {/* HEADER */}
        <HUText fontStyle="TL" className="text-center">
          {title}
        </HUText>

        {/* CONTENT */}
        <HUText
          fontStyle="BM"
          className="text-center"
          color={theme.neutralColor600}
          style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
        >
          {message}
        </HUText>

        {/* BUTTON */}
        <div className="mb-3 flex flex-row gap-4 align-items-center justify-content-center">
          <HUButton
            type="Left_Icon"
            size="M"
            colorType={currentWeekHasSessions ? "Secondary" : "Quaternary"}
            text={
              currentWeekHasSessions
                ? word("program.weekCopyPaste.paste.modale.sessionsAlreadyExist.replace")
                : word("global.cancel")
            }
            onClick={async (event) => {
              event.preventDefault()
              if (currentWeekHasSessions) {
                await onPasteWeek(true)
              }
              setShowModal(false)
            }}
            className="w-full"
          />
          <HUButton
            type="Right_Icon"
            size="M"
            colorType={currentWeekHasSessions ? "Secondary" : "Primary"}
            text={
              currentWeekHasSessions
                ? word("program.weekCopyPaste.paste.modale.sessionsAlreadyExist.keep")
                : word("global.paste")
            }
            onClick={async (event) => {
              event.preventDefault()
              await onPasteWeek(false)
            }}
            disabled={isSubmitting}
            className="w-full"
          />
        </div>
      </div>
    </HUDialog>
  )
}
